import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const ICONS = {
  church: "/app-icons/oikos-church-icon.png",
  falcon: "/app-icons/falcon-icon.png",
  liveDisplay: "/app-icons/church-live-display-icon.png",
  management: "/app-icons/oikos-management-icon.png",
  sermon: "/app-icons/sermon-icon.png",
};

const CHURCH_ORG_SESSION_KEY = "oikos.church.orgUserSession";
const CHURCH_ORG_CACHE_KEY = "oikos.organization.church";
const INSTALL_LAUNCH_PATH_KEY = "oikos.install.launchPath";
const ORG_QUERY_PARAM = "org";
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function upsertMeta(name, content) {
  let element = document.querySelector(`meta[name="${name}"]`);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("name", name);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
}

function upsertLink(rel, href, attributes = {}) {
  let element = document.querySelector(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function getIconType(iconPath) {
  if (iconPath.toLowerCase().endsWith(".png")) return "image/png";
  if (iconPath.toLowerCase().endsWith(".ico")) return "image/x-icon";
  return "image/svg+xml";
}

function absoluteUrl(path) {
  return new URL(path || "/", window.location.origin).toString();
}

function makeLocationLikeFromPath(path = "", fallbackLocation = {}) {
  try {
    const url = new URL(path || "/", window.location.origin);
    return {
      pathname: url.pathname || fallbackLocation.pathname || "/",
      search: url.search || "",
      hash: url.hash || "",
    };
  } catch (_error) {
    return fallbackLocation;
  }
}

function isCustomChurchDomain() {
  if (typeof window === "undefined") return false;

  const hostname = window.location.hostname.toLowerCase();
  return (
    hostname.endsWith(".oikoschurch.app") &&
    hostname !== "www.oikoschurch.app"
  );
}

function readJsonLocalStorage(key) {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_error) {
    return null;
  }
}

function getExistingOrgParam(search = "") {
  try {
    const params = new URLSearchParams(search);
    return (
      params.get(ORG_QUERY_PARAM) ||
      params.get("accountId") ||
      params.get("account") ||
      params.get("churchId") ||
      params.get("church")
    );
  } catch (_error) {
    return "";
  }
}

function getTrailingChurchOrgId(pathname = "") {
  const parts = String(pathname || "")
    .split("/")
    .filter(Boolean);
  const tail = parts.length ? decodeURIComponent(parts[parts.length - 1]) : "";
  return UUID_PATTERN.test(tail) ? tail : "";
}

function stripTrailingChurchOrgId(pathname = "") {
  const orgId = getTrailingChurchOrgId(pathname);
  return orgId ? pathname.slice(0, -(orgId.length + 1)) || "/" : pathname;
}

function supportsTrailingChurchOrgId(pathname = "") {
  const cleanPath = stripTrailingChurchOrgId(pathname);

  return (
    cleanPath === "/church" ||
    cleanPath === "/church/login" ||
    cleanPath === "/church/management" ||
    cleanPath === "/church/management/minutes-pad" ||
    cleanPath === "/sermon" ||
    cleanPath === "/sermon/editor" ||
    cleanPath === "/live-display" ||
    /^\/church\/controllers\/[^/]+$/.test(cleanPath)
  );
}

function readStoredChurchAccountId(search = "") {
  const existingOrg = getExistingOrgParam(search);
  if (existingOrg) return existingOrg;

  if (typeof window !== "undefined") {
    const pathOrg = getTrailingChurchOrgId(window.location.pathname);
    if (pathOrg) return pathOrg;
  }

  const orgSession = readJsonLocalStorage(CHURCH_ORG_SESSION_KEY);
  const sessionAccountId = orgSession?.account?.id || orgSession?.account_id || "";
  if (sessionAccountId) return sessionAccountId;

  const cachedAccount = readJsonLocalStorage(CHURCH_ORG_CACHE_KEY);
  return cachedAccount?.id || "";
}

function shouldAttachChurchOrg(pathname) {
  return (
    supportsTrailingChurchOrgId(pathname) ||
    pathname === "/church" ||
    pathname === "/sermon" ||
    pathname === "/live-display"
  );
}

function isSafeRedirectPath(redirectPath = "") {
  return (
    redirectPath &&
    redirectPath.startsWith("/") &&
    !redirectPath.startsWith("//") &&
    redirectPath !== "/church/login" &&
    !redirectPath.startsWith("/church/login/")
  );
}

function getRedirectFromSearch(search = "") {
  try {
    const params = new URLSearchParams(search || "");
    const redirectPath = params.get("redirect") || params.get("next") || "";
    return isSafeRedirectPath(redirectPath) ? redirectPath : "";
  } catch (_error) {
    return "";
  }
}

function getInstallLocation(location) {
  const redirectPath = location.pathname.startsWith("/church/login")
    ? getRedirectFromSearch(location.search)
    : "";

  if (redirectPath && isAppLaunchPath(makeLocationLikeFromPath(redirectPath, location).pathname)) {
    return makeLocationLikeFromPath(redirectPath, location);
  }

  return location;
}

function buildStartPath(location, churchAccountId = "") {
  const params = new URLSearchParams(location.search || "");

  if (isCustomChurchDomain()) {
    return `${location.pathname}${location.search || ""}${location.hash}`;
  }

  if (churchAccountId && supportsTrailingChurchOrgId(location.pathname)) {
    const cleanPath = stripTrailingChurchOrgId(location.pathname);
    return `${cleanPath}/${encodeURIComponent(churchAccountId)}${location.search || ""}${location.hash}`;
  }

  if (churchAccountId && shouldAttachChurchOrg(location.pathname) && !params.get(ORG_QUERY_PARAM)) {
    params.set(ORG_QUERY_PARAM, churchAccountId);
  }

  const nextSearch = params.toString() ? `?${params.toString()}` : "";
  return `${location.pathname}${nextSearch}${location.hash}`;
}

function isAppLaunchPath(pathname = "") {
  return Boolean(pathname && pathname !== "/" && !pathname.startsWith("/church/site"));
}

function getControllerName(pathname) {
  if (pathname.endsWith("/preacher")) return "Preacher Controller";
  if (pathname.endsWith("/hymns")) return "Hymn Controller";
  return "Main Controller";
}

function getInstallProfile(pathname) {
  if (pathname.startsWith("/church/controllers")) {
    const controllerName = getControllerName(pathname);
    return {
      name: `Oikos Church ${controllerName}`,
      shortName: controllerName,
      icon: ICONS.church,
      appleIcon: ICONS.church,
      themeColor: "#355f43",
      orientation: "any",
    };
  }

  if (pathname.startsWith("/live/")) {
    return {
      name: "Oikos Church Live Display",
      shortName: "Live Display",
      icon: ICONS.liveDisplay,
      appleIcon: ICONS.liveDisplay,
      themeColor: "#ffffff",
      orientation: "landscape",
    };
  }

  if (pathname.startsWith("/live-display")) {
    return {
      name: "Oikos Church Live Display",
      shortName: "Live Display",
      icon: ICONS.liveDisplay,
      appleIcon: ICONS.liveDisplay,
      themeColor: "#5F7D4D",
      orientation: "any",
    };
  }

  if (pathname.startsWith("/sermons")) {
    return {
      name: "Oikos Sermons",
      shortName: "Sermons",
      icon: ICONS.falcon,
      appleIcon: ICONS.falcon,
      themeColor: "#385cff",
      orientation: "any",
    };
  }

  if (pathname.startsWith("/sermon")) {
    return {
      name: "Oikos Church Sermon",
      shortName: "Sermon",
      icon: ICONS.sermon,
      appleIcon: ICONS.sermon,
      themeColor: "#385cff",
      orientation: "any",
    };
  }

  if (pathname.startsWith("/hymns")) {
    return {
      name: "Oikos Church Hymns",
      shortName: "Hymns",
      icon: ICONS.church,
      appleIcon: ICONS.church,
      themeColor: "#5F7D4D",
      orientation: "any",
    };
  }

  if (pathname.startsWith("/church/management")) {
    return {
      name: "Oikos Church Management",
      shortName: "Management",
      icon: ICONS.management,
      appleIcon: ICONS.management,
      themeColor: "#5F7D4D",
      orientation: "any",
    };
  }

  if (pathname.startsWith("/church")) {
    return {
      name: "Oikos Church",
      shortName: "Church",
      icon: ICONS.church,
      appleIcon: ICONS.church,
      themeColor: "#5F7D4D",
      orientation: "any",
    };
  }

  return {
    name: "Oikos Church",
    shortName: "Church",
    icon: ICONS.church,
    appleIcon: ICONS.church,
    themeColor: "#5F7D4D",
    orientation: "any",
  };
}

export default function DynamicInstallMetadata() {
  const location = useLocation();
  const [churchAccountId, setChurchAccountId] = useState(() =>
    readStoredChurchAccountId(location.search)
  );

  useEffect(() => {
    const refreshChurchAccountId = (event) => {
      const nextAccountId =
        event?.detail?.accountId || readStoredChurchAccountId(window.location.search);
      if (nextAccountId) {
        setChurchAccountId(nextAccountId);
      }
    };

    refreshChurchAccountId();
    window.addEventListener("oikos-theme-change", refreshChurchAccountId);
    window.addEventListener("storage", refreshChurchAccountId);

    return () => {
      window.removeEventListener("oikos-theme-change", refreshChurchAccountId);
      window.removeEventListener("storage", refreshChurchAccountId);
    };
  }, []);

  useEffect(() => {
    const installLocation = getInstallLocation(location);
    const startUrl = buildStartPath(installLocation, churchAccountId);
    const profile = getInstallProfile(installLocation.pathname);
    const absoluteStartUrl = absoluteUrl(startUrl || "/");
    const absoluteIcon = absoluteUrl(profile.icon);
    const absoluteAppleIcon = absoluteUrl(profile.appleIcon || profile.icon);
    const manifest = {
      id: absoluteStartUrl,
      name: profile.name,
      short_name: profile.shortName,
      start_url: absoluteStartUrl,
      scope: `${window.location.origin}/`,
      display: "standalone",
      orientation: profile.orientation,
      theme_color: profile.themeColor,
      background_color: profile.themeColor,
      icons: [
        {
          src: absoluteIcon,
          sizes: "1254x1254",
          type: getIconType(profile.icon),
          purpose: "any maskable",
        },
        ...(profile.appleIcon
          ? [
              {
                src: absoluteAppleIcon,
                sizes: "1254x1254",
                type: getIconType(profile.appleIcon),
                purpose: "any maskable",
              },
            ]
          : []),
      ],
    };
    const manifestBlob = new Blob([JSON.stringify(manifest)], {
      type: "application/manifest+json",
    });
    const manifestUrl = URL.createObjectURL(manifestBlob);

    document.title = profile.name;
    upsertLink("manifest", manifestUrl);
    upsertLink("icon", profile.icon, { type: getIconType(profile.icon) });
    upsertLink("shortcut icon", profile.icon, { type: getIconType(profile.icon) });
    upsertLink("apple-touch-icon", profile.appleIcon || profile.icon);
    upsertMeta("theme-color", profile.themeColor);
    upsertMeta("mobile-web-app-capable", "yes");
    upsertMeta("apple-mobile-web-app-capable", "yes");
    upsertMeta("apple-mobile-web-app-title", profile.shortName);
    upsertMeta("apple-mobile-web-app-status-bar-style", "black-translucent");

    if (!isCustomChurchDomain() && isAppLaunchPath(installLocation.pathname)) {
      try {
        window.localStorage.setItem(INSTALL_LAUNCH_PATH_KEY, startUrl || "/");
      } catch (_error) {
        // Safari private mode can block localStorage.
      }
    }

    if (
      churchAccountId &&
      shouldAttachChurchOrg(location.pathname) &&
      !getExistingOrgParam(location.search)
    ) {
      window.history.replaceState(window.history.state, "", startUrl);
    }

    return () => {
      URL.revokeObjectURL(manifestUrl);
    };
  }, [churchAccountId, location.hash, location.pathname, location.search]);

  return null;
}
